import { StatePage } from "@/components/state-page";
import { filterKeys } from "@/utils";
import { orpc } from "@/utils/clients/orpc";
import { CONSTANTS } from "@/utils/constants";
import { requirePermissions } from "@/utils/permissions";
import type { APIOutput } from "@/utils/types";
import { Button } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { t } from "i18next";
import { LucideDownload } from "lucide-react";
import ControlBar from "./-control-bar";
import AuditLogList from "./-list";
import { AuditLogFiltersSchema, DEFAULT_AUDIT_LOG_PARAMS } from "./-utils";

export const Route = createFileRoute("/_authenticated/audit-log/")({
  validateSearch: AuditLogFiltersSchema,
  component: Component,
  beforeLoad: requirePermissions(true),
  loaderDeps: ({ search }) => [search] as const,
  loader: ({ context, deps: [search] }) => {
    void context.queryClient.prefetchQuery(
      orpc.auditLog.getAll.queryOptions({
        input: { ...DEFAULT_AUDIT_LOG_PARAMS, ...search },
      }),
    );
  },
  errorComponent: ({ error, reset }) => (
    <StatePage error={error} reset={reset} resourceType="auditLog" emptyResourceType="auditLog" />
  ),
  notFoundComponent: () => <StatePage isNotFound resourceType="auditLog" emptyResourceType="auditLog" />,
});

function Component() {
  const search = useSearch({ from: "/_authenticated/audit-log/" });
  const queryClient = useQueryClient();
  const filters = filterKeys(search, AuditLogFiltersSchema.shape);

  const exportMutation = useMutation({
    mutationFn: async () => {
      const [{ pdf }, { AuditLogPDFDocument }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("./-pdf-document"),
      ]);

      const agency = await queryClient.fetchQuery(orpc.agency.get.queryOptions());

      // Fetch all audit log entries with pagination
      const allLogs: APIOutput["auditLog"]["getAll"] = [];
      let page = DEFAULT_AUDIT_LOG_PARAMS.page;
      const limit = 1000;
      let hasMore = true;

      while (hasMore) {
        const logs = await queryClient.fetchQuery(
          orpc.auditLog.getAll.queryOptions({
            input: {
              ...DEFAULT_AUDIT_LOG_PARAMS,
              ...filters,
              limit,
              page,
            },
          }),
        );

        allLogs.push(...logs);

        if (logs.length < limit) {
          hasMore = false;
        } else {
          page++;
        }
      }

      if (allLogs.length === 0) {
        throw new Error(t("auditLog.exportPDF.all.empty"));
      }

      const doc = (
        <AuditLogPDFDocument
          logs={allLogs}
          agency={agency}
          baseUrl={window.location.origin}
          dateRange={{
            startDate: filters.dateMin,
            endDate: filters.dateMax,
          }}
        />
      );

      const blob = await pdf(doc).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const dateStr = new Date().toISOString().split("T")[0];
      link.download = `audit-log-${dateStr}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      notifications.show({
        title: t("success"),
        message: t("auditLog.exportPDF.all.success.description"),
        color: "green",
      });
    },
    onError: (error) => {
      notifications.show({
        title: t("error.title"),
        message: error instanceof Error ? error.message : t("auditLog.exportPDF.all.error.description"),
        color: "red",
      });
    },
  });

  return (
    <div className="gap-md md:gap-lg flex grow flex-col">
      <div className="py-md flex flex-wrap items-center justify-between gap-x-6 gap-y-4 md:justify-start">
        <p className="text-2xl font-semibold md:text-3xl">{t("auditLog.title")}</p>
        <Button
          variant="default"
          leftSection={<LucideDownload className="size-4" />}
          onClick={() => exportMutation.mutate()}
          loading={exportMutation.isPending}
          disabled={CONSTANTS.DEMO}
        >
          {t("export")}
        </Button>
      </div>
      <ControlBar />
      <AuditLogList mode="audit-log" />
    </div>
  );
}
